import React, { KeyboardEvent, memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import type { TabsSelectorOption } from './TabsSelector.types'

interface Props<V extends string> extends TabsSelectorOption<V> {
  selected: boolean
  onSelect(value: V): void
  dataTestId?: string
}

const TabsSelectorItemComponent = <V extends string>({
  label,
  value,
  count,
  selected,
  onSelect,
  dataTestId,
}: Props<V>) => {
  const handleSelect = useCallback(() => {
    onSelect(value)
  }, [value, onSelect])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onSelect(value)
      }
    },
    [value, onSelect],
  )

  return (
    <div
      role="tab"
      tabIndex={0}
      aria-selected={selected}
      data-testid={dataTestId}
      css={tw`mx-4 max-w-[240px] cursor-pointer hover:opacity-70`}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
    >
      <div css={tw`flex items-center`}>
        <Typography
          variant="subtitle1"
          css={[
            tw`overflow-hidden text-ellipsis whitespace-nowrap`,
            selected ? tw`text-shades-1000` : tw`text-shades-500`,
          ]}
        >
          {label}
        </Typography>
        {count ? (
          <div
            css={[
              tw`ml-2 rounded-[10px] px-2 py-0.5`,
              selected ? tw`bg-shades-200` : tw`bg-shades-100`,
            ]}
          >
            <Typography
              variant="subtitle2"
              css={selected ? tw`text-shades-1000` : tw`text-shades-500`}
            >
              {count}
            </Typography>
          </div>
        ) : null}
      </div>
      {/* The 3px black indicator; kept in the flow (not absolute) so every tab is the same height. */}
      <div
        css={[tw`mt-3 h-[3px] w-full rounded-t-lg bg-shades-1000`, !selected && tw`opacity-0`]}
      />
    </div>
  )
}

export const TabsSelectorItem = memo(TabsSelectorItemComponent) as typeof TabsSelectorItemComponent
